import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  fetchItems,
  fetchItem,
  createItem,
  updateItem,
  destroyItem,
  fetchStats,
  downloadCsvReport,
  searchCampaigns
} from '@/apps/traforama/api/campaignGroups'
import {
  ICampaignGroup,
  ICampaignGroupData,
  ICampaignGroupsPayload,
  ICampaignGroupStats,
  ICampaignMini
} from '@/apps/traforama/interfaces/campaignGroups'

export const useCampaignGroupsStore = defineStore('campaignGroups', () => {
  const items = ref<ICampaignGroup[]>([])
  const total = ref(0)
  const loading = ref(false)
  const statsLoading = ref(false)
  const stats = ref<Record<string, ICampaignGroupStats>>({})
  const searchResults = ref<ICampaignMini[]>([])
  const searchLoading = ref(false)
  const error = ref<string | null>(null)

  const dateFrom = ref<string>(getDefaultDateFrom())
  const dateTo = ref<string>(getDefaultDateTo())

  function getDefaultDateFrom(): string {
    const date = new Date()
    date.setDate(1)
    return date.toISOString().split('T')[0]
  }

  function getDefaultDateTo(): string {
    return new Date().toISOString().split('T')[0]
  }

  const FetchItems = async (payload?: ICampaignGroupsPayload) => {
    loading.value = true
    error.value = null
    try {
      const response = await fetchItems(payload || { limit: 100, start: 0 })
      items.value = response.data
      total.value = response.meta?.total || response.data.length
    } catch (e: any) {
      error.value = e.toString()
    } finally {
      loading.value = false
    }
  }

  const FetchStats = async (groupIds?: number[]) => {
    statsLoading.value = true
    try {
      const response = await fetchStats(dateFrom.value, dateTo.value, groupIds)
      stats.value = response
    } catch (e: any) {
      error.value = e.toString()
    } finally {
      statsLoading.value = false
    }
  }

  const CreateItem = async (data: ICampaignGroupData) => {
    loading.value = true
    error.value = null
    try {
      const response = await createItem(data)
      items.value.unshift(response.data)
      total.value++
      return response.data
    } catch (e: any) {
      error.value = e.toString()
      throw e
    } finally {
      loading.value = false
    }
  }

  const UpdateItem = async (id: number, data: ICampaignGroupData) => {
    loading.value = true
    error.value = null
    try {
      const response = await updateItem(id, data)
      const index = items.value.findIndex((item) => parseInt(item.id) === id)
      if (index !== -1) {
        items.value[index] = response.data
      }
      return response.data
    } catch (e: any) {
      error.value = e.toString()
      throw e
    } finally {
      loading.value = false
    }
  }

  const DestroyItem = async (id: number) => {
    loading.value = true
    error.value = null
    try {
      await destroyItem(id)
      items.value = items.value.filter((item) => parseInt(item.id) !== id)
      total.value--
    } catch (e: any) {
      error.value = e.toString()
      throw e
    } finally {
      loading.value = false
    }
  }

  const DownloadCsvReport = async (id: number) => {
    try {
      const blob = await downloadCsvReport(id, dateFrom.value, dateTo.value)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `campaign_group_report_${id}_${dateTo.value}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      error.value = e.toString()
      throw e
    }
  }

  const SearchCampaigns = async (query: string) => {
    searchLoading.value = true
    try {
      const response = await searchCampaigns(query)
      searchResults.value = response.data
    } catch (e: any) {
      error.value = e.toString()
    } finally {
      searchLoading.value = false
    }
  }

  const SetDateRange = (from: string, to: string) => {
    dateFrom.value = from
    dateTo.value = to
  }

  const itemById = (id: number) => {
    return items.value.find((item) => parseInt(item.id) === id)
  }

  return {
    items,
    total,
    loading,
    statsLoading,
    stats,
    searchResults,
    searchLoading,
    error,
    dateFrom,
    dateTo,
    FetchItems,
    FetchStats,
    CreateItem,
    UpdateItem,
    DestroyItem,
    DownloadCsvReport,
    SearchCampaigns,
    SetDateRange,
    itemById
  }
})
